import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Target, CaretDown, CaretUp } from "@phosphor-icons/react";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};
const pctColor = (p) => (Math.abs(p) <= 5 ? "text-positive" : Math.abs(p) <= 12 ? "text-warn" : "text-red-600");

export default function AccrualAccuracy({ standalone, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(Boolean(standalone));

  useEffect(() => {
    api.get("/accruals/recurring/variance").then(({ data }) => setData(data)).catch(() => setData(null));
  }, [refreshKey]);

  if (!data || (data.back_test.length === 0 && data.posted.length === 0)) {
    if (!standalone) return null;
    return (
      <div className="card-surface p-12 text-center" data-testid="accuracy-empty">
        <div className="w-14 h-14 rounded-2xl bg-positive/10 flex items-center justify-center mx-auto mb-4"><Target size={28} weight="fill" className="text-positive" /></div>
        <div className="font-heading font-bold text-xl text-navy">No accuracy data yet</div>
        <div className="text-sm text-slate-600 mt-1.5">Once recurring accruals are posted and the actual bills arrive, prediction accuracy appears here.</div>
      </div>
    );
  }
  const acc = data.summary.accuracy;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl mb-5 shadow-card" data-testid="accrual-accuracy-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-lg bg-positive/10 text-positive flex items-center justify-center"><Target size={16} weight="fill" /></span>
          <div className="text-left">
            <div className="font-heading font-bold text-navy text-base tracking-tight">Forecast accuracy</div>
            <div className="text-xs text-navy/70">How close our accrual predictions are to the actual bills</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {acc != null && (
            <div className="text-right" data-testid="accuracy-summary">
              <div className="font-heading font-black text-2xl text-navy leading-none tabular">{acc}%</div>
              <div className="overline text-navy/65">accuracy · {data.summary.count} mths</div>
            </div>
          )}
          {open ? <CaretUp size={16} className="text-navy/65" /> : <CaretDown size={16} className="text-navy/65" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          {data.posted.some((p) => p.status === "pending") && (
            <div className="text-xs text-navy/70 mb-3">
              {data.posted.filter((p) => p.status === "pending").length} posted accrual(s) awaiting the actual bill to compare.
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="overline text-navy/65 border-b border-slate-100">
                <th className="text-left py-2">Supplier</th><th className="text-left py-2">Month</th>
                <th className="text-right py-2">Predicted</th><th className="text-right py-2">Actual</th><th className="text-right py-2">Variance</th>
              </tr>
            </thead>
            <tbody>
              {data.back_test.map((b, i) => (
                <tr key={i} className="border-b border-navy/5" data-testid={`variance-row-${i}`}>
                  <td className="py-2.5 text-navy">{b.contact}<span className="text-navy/65 text-xs"> · {b.account_code}</span></td>
                  <td className="py-2.5 text-navy/70">{monthLabel(b.month)}</td>
                  <td className="py-2.5 text-right font-mono tabular text-navy/70">{money(b.predicted)}</td>
                  <td className="py-2.5 text-right font-mono tabular text-navy">{money(b.actual)}</td>
                  <td className={`py-2.5 text-right font-mono tabular font-semibold ${pctColor(b.variance_pct)}`}>
                    {b.variance_pct > 0 ? "+" : ""}{b.variance_pct}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
